import { useState, useMemo, useEffect } from 'react';

import { useSelector } from '../../Hooks';
import { checkOutOfStock } from '../../utils/inventory';
import useI18n from '../../i18n';
import { useGetModificationGroups } from './api/useGetModificationGroups';
import { ModificationGroupFragment_variations } from './api/__queries__';

export interface LocalVariation {
  id: string;
  name: string;
  price: number;
}

export enum MODIFICATION_GROUP_TYPE {
  MULTIPLE = 'MULTIPLE',
  SINGLE = 'SINGLE',
}

export interface Variation {
  id: string;
  name: string;
  selected: boolean;
  price: number;
  imageUrl: string | undefined;
  isDefault: boolean;
  isOutOfStock?: boolean;
}

/** This is not the raw ModificationGroup type — we add extra fields like `valid` which is calculated inside the hook */
export interface FormattedModificationGroup {
  id: string;

  /** The external name of the modification group */
  title: string;

  /** The type of modification group (SINGLE or MULTIPLE) */
  type: MODIFICATION_GROUP_TYPE;

  /** All the variations that belong to this group for this given group */
  variations: Variation[];

  /** If this is true - At least one variation must be selected */
  required: boolean;

  /** Is this group valid? If any group is invalid, we block adding to cart (e.g., required group with no selection) */
  valid: boolean;

  /** If this group is attached to an item, the ItemModifications.tsx popup will open when pressing the item in POS */
  expandedByDefault: boolean;

  /** Which variations should be preselected.
   * Only applies to SINGLE groups (one can be preselected) or MULTIPLE if it's a removable ingredient group (then all are preselected).
   */
  preselectedVariations: string[];
}

/**
 * Helper hook to:
 * - Get an items variations & their selections
 * - Handle the selection of variations
 *
 * Info: A modification group is a wrapper group around variations to give information about how it should be displayed and handled
 * @param modificationGroupIds - Array of modification group IDs (modificationGroups is saved under Item)
 */
const useItemModifications = (modificationGroupIds: string[]) => {
  const { i18n } = useI18n();

  const inventory = useSelector((state) => state.inventory);

  const modificationGroups = useGetModificationGroups(modificationGroupIds);
  const allVariations = modificationGroups.flatMap((mg) => mg.variations);

  /**
   * This is variations that should already be selected when opening the item.
   * If the group is MULTIPLE (meaning multiple variations can be selected), then the preselected variations should never be a thing
   * unless the group is a removable ingredients group (but we have code for that later in the hook)
   */
  const preselectedVariations = useMemo(
    () =>
      modificationGroups
        .filter((mg) => mg.type === MODIFICATION_GROUP_TYPE.MULTIPLE)
        .flatMap((mg) =>
          mg.preselectedVariations
            .map((id) => mg.variations.find((v) => v.id === id))
            .filter((v): v is ModificationGroupFragment_variations => !!v),
        ),
    [modificationGroups],
  );

  const preselectedWithoutOutOfStock = preselectedVariations.filter((v) =>
    checkOutOfStock(v.asItem?.id || v.id, inventory, true),
  );

  const [selectedVariationIds, setSelectedVariationIds] = useState<string[]>(
    preselectedWithoutOutOfStock.map((v) => v.id),
  );

  /* Update the selected variations when the initial selection changes */
  useEffect(() => {
    if (modificationGroups) {
      /* Loop through each modificationGroup and get its preselectedVariations */
      const newSelectedVariations = modificationGroups
        .filter((mg) => mg.type === 'SINGLE')
        .reduce(
          (acc, mg) => {
            mg.preselectedVariations.forEach((pv) => {
              if (!acc.includes(pv)) {
                acc.push(pv);
              }
            });
            return acc;
          },
          [...selectedVariationIds],
        );

      // Set the updated selected variations if there are new ones
      setSelectedVariationIds(newSelectedVariations);
    }
  }, []);

  /* Check if a variation is configured as a preselected somewhere in any of the groups that has been passed in the hook */
  const isPreselectedInAny = (id: string) => preselectedVariations.some((dv) => dv.id === id);

  /* Check if this is currently selected */
  const isSelected = (id: string) => selectedVariationIds.includes(id);

  /** Runs when a variation has been touched */
  const onSelectVariation = (variationId: string) => {
    const isThisVariationSelected = isSelected(variationId);

    /* Find the group that the variation belongs to */
    const group = modificationGroups.find((mg) => mg.variations.some((v) => v.id === variationId));

    /* If it's a single group and we are clicking an option that was already selected, do nothing. */
    if (isThisVariationSelected && group?.type === MODIFICATION_GROUP_TYPE.SINGLE) {
      return;
    }

    /* If it's a multi group and it's already selected, then unmark it as selected*/
    if (isThisVariationSelected) {
      setSelectedVariationIds(selectedVariationIds.filter((svID) => svID !== variationId));
      return;
    }

    /* If it's not selected and it's a single group, we need to first remove the other selected before selecting since only one can be selected in this group */
    if (group?.type === MODIFICATION_GROUP_TYPE.SINGLE) {
      /* We keep the existing selections that are not part of this group.
       * This is important since some of them may be out-of-stock variations, which are not selectable but still present.
       */
      const idsInGroup = group?.variations.map((vari) => vari.id) || [];
      const keepSelections = selectedVariationIds.filter((svID) => !idsInGroup.includes(svID));

      setSelectedVariationIds([...keepSelections, variationId]);
      return;
    }

    /* If its not selected & MULTIPLE - Just add selection to the group */
    setSelectedVariationIds([...selectedVariationIds, variationId]);
  };

  /* Format our state to something that is easily consumable by the client */
  const formattedModificationGroups = useMemo(
    () =>
      modificationGroups.map((mg) => {
        let atLeastOneSelected = false;
        const variations = mg.variations.map((vari) => {
          const selected = isSelected(vari.id);
          const isPreselectedVariation = isPreselectedInAny(vari.id);

          /* Inverse selection set for default variations. A selected default variation is actually an un-selection */
          const markAsSelected = isPreselectedVariation ? !selected : selected;

          atLeastOneSelected = markAsSelected || atLeastOneSelected;

          /* Check if the variation is present in the inventory universe, and if it is, add the inventory metadata to the variation */
          const isOutOfStock = checkOutOfStock(vari.asItem?.id || vari.id, inventory, true);
          const variName = i18n(vari.nameLang);

          return {
            id: vari.id,
            name: variName.length > 0 ? variName : vari.name,
            price: vari.price,
            selected: markAsSelected,
            imageUrl: vari.image?.file.url,
            isDefault: isPreselectedVariation,
            isOutOfStock,
          };
        });
        return {
          id: mg.id,
          title: i18n(mg.externalName),
          variations,
          type: mg.type,
          expandedByDefault: mg.expandedByDefault,
          required: mg.required,
          valid: !mg.required || (mg.required && atLeastOneSelected),
          preselectedVariations: mg.preselectedVariations,
        };
      }),
    [selectedVariationIds, modificationGroups],
  );

  /* Find all removed variations and convert them to local (i18n) format */
  const removedVariations: LocalVariation[] = useMemo(
    () => [
      ...preselectedVariations
        .filter((defVari) => selectedVariationIds.some((id) => id === defVari.id))
        .map((rv) => ({
          ...rv,
          name: i18n(rv.nameLang),
        })),
    ],
    [selectedVariationIds],
  );

  /* Map the selected variations & exclude the preselected ones */
  const formattedSelectedVariations = selectedVariationIds
    .filter((variId) =>
      allVariations.some(
        (vari) => vari.id === variId && !preselectedVariations.some((pv) => pv.id === variId),
      ),
    )
    .map((id) => {
      const variation = allVariations.find((v) => v.id === id)!;

      if (variation) {
        return {
          id: variation.id,
          name: i18n(variation.nameLang),
          price: variation.price,
        };
      } else
        return {
          id: '',
          name: '',
          price: 0,
        };
    });

  return {
    onSelectVariation,
    modificationGroups: formattedModificationGroups,
    selectedVariations: formattedSelectedVariations,
    removedVariations,
  };
};

export default useItemModifications;
